import React, { useMemo } from 'react';
import { IModalProps } from '@cp/shared/modal';
import {
  MODAL_MOBILE_BREAKPOINT_THRESHOLD,
  ModalContainer,
  ModalHeader,
  ModalLayout,
  ModalLayoutTokens,
} from '@cp/ds/src/components/modalLayout';
import { useDeviceLocation } from '@cp/shared/location';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Stack } from '@cp/ds/src/components/stack';
import { AppFormProvider } from '@cp/utils/form';
import { Box } from '@cp/ds/src/components/box';
import { GeoPoint } from '@common/model/location/geopoint';
import { CITY_SEARCH_FORM_FIELD_NAME_DEFAULT, CitySearchFormField, getMapLocation } from '@cp/entities/location';
import { useIsMobile, useTheme } from '@cp/ds/src/hooks';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { LocationSuggestion } from '@common/model/location/locationSuggestion';
import { CareerIcon } from '@cp/entities/career';
import { Button } from '@cp/ds/src/components/button';
import { ArrowRightIcon } from '@cp/ds/src/icons';
import { PlaceholderedMap, useMapMarkers } from 'src/shared/ui/map';
import { CatalogItemsCounter, useGetLocations } from 'src/entities/catalog';
import { ClientSelection } from 'src/entities/client';

type LocationFormModel = {
  city?: CityViewModel;
  selectedAddress?: LocationSuggestion;
  location?: GeoPoint;
};
export interface LocationModalProps extends IModalProps<Pick<ClientSelection, 'cityId' | 'address' | 'location'>> {
  clientSelection: ClientSelection;
}
export const LocationModal: React.FC<LocationModalProps> = ({ onCancel, onSuccess, clientSelection }) => {
  const theme = useTheme();
  const deviceLocation = useDeviceLocation();
  const isMobile = useIsMobile();
  const form = useForm<LocationFormModel>();
  const [city, selectedAddress] = form.watch(['city', 'selectedAddress']);
  const citySelected = Boolean(city);

  const locations = useGetLocations(clientSelection.catalogType, clientSelection.careerId, city?.id);
  const { markers } = useMapMarkers(locations);

  const mapCenter = useMemo(() => {
    const location = getMapLocation(selectedAddress, city, deviceLocation);
    return location ? GeoPoint.create({ lat: location.lat - 0.1, long: location.long }) : undefined;
  }, [deviceLocation, city, selectedAddress]);

  const handleSubmit: SubmitHandler<LocationFormModel> = (values) => {
    const cityFromValues = values[CITY_SEARCH_FORM_FIELD_NAME_DEFAULT];

    if (!cityFromValues) {
      form.setError(CITY_SEARCH_FORM_FIELD_NAME_DEFAULT, {
        type: 'validation',
        message: 'Необходимо выбрать город',
      });
      return;
    }

    return onSuccess({
      cityId: cityFromValues.id,
      address: values.selectedAddress?.address,
      location: getMapLocation(values.selectedAddress, cityFromValues, deviceLocation)?.toString(),
    });
  };

  return (
    <ModalLayout
      containerSx={{
        [ModalLayoutTokens.ContainerWidth]: '800px',
        ...(!citySelected && isMobile && { [ModalLayoutTokens.PaddingBottom]: 0 }),
      }}
      icon={!isMobile && <CareerIcon careerId={clientSelection.careerId} />}
      onClose={onCancel}
    >
      <AppFormProvider form={form}>
        <Stack
          alignItems="stretch"
          component="form"
          direction="column"
          gap="30px"
          justifyContent="stretch"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <ModalContainer sx={{ pt: '20px' }}>
            <Stack spacing={2.5} sx={{ alignItems: { xs: 'stretch', [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: 'center' } }}>
              <ModalHeader>Город, где предстоит работать</ModalHeader>
              <CitySearchFormField fieldContainerProps={{ sx: { flexGrow: 1, width: '100%' } }} placeholder="Город" />
            </Stack>
          </ModalContainer>
          <Box sx={{ position: 'relative' }}>
            <PlaceholderedMap
              center={mapCenter}
              fullscreenControl={false}
              markers={markers}
              sx={{
                height: 345,
                width: { xs: 1, [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: 700 },
                borderRadius: { xs: 0, [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: `${theme.borderRadius.normal}px` },
                overflow: 'hidden',
              }}
              zoomControl={false}
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
              }}
            >
              {clientSelection.careerId && clientSelection.clientType && city ? (
                <CatalogItemsCounter careerId={clientSelection.careerId} cityId={city.id} clientType={clientSelection.clientType} />
              ) : null}
            </Box>
          </Box>
          <ModalContainer sx={{ justifyContent: 'center', display: citySelected ? 'flex' : 'none' }}>
            <Button
              iconRight={isMobile ? undefined : <ArrowRightIcon />}
              sx={{
                width: { xs: 1, [MODAL_MOBILE_BREAKPOINT_THRESHOLD]: 'auto' },
              }}
              type="submit"
              variant={'primary'}
            >
              Далее
            </Button>
          </ModalContainer>
        </Stack>
      </AppFormProvider>
    </ModalLayout>
  );
};
